import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";

import {
    FaSyncAlt,
    FaMapMarkerAlt,
    FaChargingStation,
    FaWalking,
    FaCheckCircle,
    FaBan,
    FaArrowLeft,
    FaDownload
} from "react-icons/fa";

import "./RobotFleet.css";
import "./RobotDetail.css";

import { getMenuPermissions } from "../utils/auth";
import { StatusPill, BatteryBar, OutcomeBadge } from "../components/RobotStatusUI";

const API_URL = process.env.REACT_APP_API_URL;

const FLEET_ENDPOINT = `${API_URL}/portal/robots/fleet`;

function dailyReportEndpoint(robotSn, dateStr) {

    return `${API_URL}/portal/robots/${encodeURIComponent(robotSn)}/daily-report?report_date=${dateStr}`;

}

function commandHistoryEndpoint(robotSn) {

    return `${API_URL}/portal/robots/${encodeURIComponent(robotSn)}/commands`;

}

function goChargingEndpoint(robotSn) {

    return `${API_URL}/portal/robots/${encodeURIComponent(robotSn)}/commands/go-charging`;

}

function navigateEndpoint(robotSn) {

    return `${API_URL}/portal/robots/${encodeURIComponent(robotSn)}/commands/navigate`;

}

function formatDateTime(value) {

    if (!value) {
        return "-";
    }

    return new Date(value).toLocaleString();

}

function formatTime(value) {

    if (!value) {
        return "-";
    }

    return new Date(value).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

}

function formatDuration(seconds) {

    if (seconds === null || seconds === undefined) {
        return "-";
    }

    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);

    if (h > 0) {
        return `${h}h ${m}m`;
    }

    return `${m}m`;

}

function getTodayDateString() {

    const d = new Date();

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;

}

// Turns the interaction log into a CSV file and triggers a browser
// download - client-side only, no new backend endpoint needed since
// the data's already loaded on this page.
function downloadInteractionsCsv(robot, reportDate, interactions) {

    const header = ["Time", "Question", "Answer", "Emergency", "Navigate To"];

    const rows = interactions.map(item => [

        formatDateTime(item.created_at),
        item.passenger_question || "",
        item.robot_answer || "",
        item.was_emergency ? item.emergency_type || "yes" : "",
        item.navigate_to || ""

    ]);

    const escapeCsv = (value) => `"${String(value).replace(/"/g, '""')}"`;

    const csv = [header, ...rows]
        .map(row => row.map(escapeCsv).join(","))
        .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `${robot.robot_sn}_interactions_${reportDate}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

}

function RobotDetail() {

    const { robotSn } = useParams();

    const navigate = useNavigate();

    const perms = getMenuPermissions("robot_fleet");

    const [robot, setRobot] = useState(null);

    const [robotLoading, setRobotLoading] = useState(true);

    const [robotError, setRobotError] = useState(null);

    const [reportDate, setReportDate] = useState(getTodayDateString());

    const [report, setReport] = useState(null);

    const [loading, setLoading] = useState(true);

    const [error, setError] = useState(null);

    const [commands, setCommands] = useState([]);

    const [sendingCharge, setSendingCharge] = useState(false);

    const [sendingNav, setSendingNav] = useState(false);

    const [commandNote, setCommandNote] = useState(null);

    // The fleet list endpoint carries this robot's live status
    // (battery, online, current task) - there's no dedicated
    // "get one robot" endpoint, so this finds it by robot_sn from
    // the same list the fleet table itself uses.
    const loadRobot = useCallback(async () => {

        setRobotLoading(true);
        setRobotError(null);

        try {

            const res = await fetch(FLEET_ENDPOINT);

            if (!res.ok) {
                throw new Error(`Request failed with status ${res.status}`);
            }

            const data = await res.json();

            const found = (data.robots || data || []).find(r => r.robot_sn === robotSn);

            if (!found) {
                throw new Error("Robot not found in the fleet list.");
            }

            setRobot(found);

        } catch (err) {

            console.error("Robot load failed:", err);

            setRobotError(err.message || "Unable to load this robot.");

            setRobot(null);

        } finally {

            setRobotLoading(false);

        }

    }, [robotSn]);

    const loadReport = useCallback(async () => {

        setLoading(true);
        setError(null);

        try {

            const res = await fetch(dailyReportEndpoint(robotSn, reportDate));

            if (!res.ok) {
                throw new Error(`Request failed with status ${res.status}`);
            }

            const data = await res.json();

            setReport(data);

        } catch (err) {

            console.error("Daily report load failed:", err);

            setError(err.message || "Unable to load daily report");

            setReport(null);

        } finally {

            setLoading(false);

        }

    }, [robotSn, reportDate]);

    const loadCommands = useCallback(async () => {

        try {

            const res = await fetch(commandHistoryEndpoint(robotSn));

            if (res.ok) {

                setCommands(await res.json());

            }

        } catch (err) {

            console.error("Command history load failed:", err);

        }

    }, [robotSn]);

    useEffect(() => {

        loadRobot();

    }, [loadRobot]);

    useEffect(() => {

        loadReport();

    }, [loadReport]);

    useEffect(() => {

        loadCommands();

    }, [loadCommands]);

    const handleSendToCharging = useCallback(async () => {

        setSendingCharge(true);
        setCommandNote(null);

        try {

            const res = await fetch(goChargingEndpoint(robotSn), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ requested_by: "admin-ui" })
            });

            if (!res.ok) {

                const body = await res.json().catch(() => ({}));

                throw new Error(body.detail || `Request failed with status ${res.status}`);

            }

            setCommandNote({ ok: true, text: "Robot instructed to head to its charging dock." });

            await loadCommands();
            await loadRobot();

        } catch (err) {

            console.error("Send to charging failed:", err);

            setCommandNote({ ok: false, text: err.message || "Failed to send command." });

        } finally {

            setSendingCharge(false);

        }

    }, [robotSn, loadCommands, loadRobot]);

    const handleSendToReception = useCallback(async () => {

        setSendingNav(true);
        setCommandNote(null);

        try {

            const res = await fetch(navigateEndpoint(robotSn), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ requested_by: "admin-ui" })
            });

            if (!res.ok) {

                const body = await res.json().catch(() => ({}));

                throw new Error(body.detail || `Request failed with status ${res.status}`);

            }

            const data = await res.json();

            setCommandNote({ ok: true, text: `Robot instructed to head to ${data.target_pos_name}.` });

            await loadCommands();
            await loadRobot();

        } catch (err) {

            console.error("Send to reception failed:", err);

            setCommandNote({ ok: false, text: err.message || "Failed to send command." });

        } finally {

            setSendingNav(false);

        }

    }, [robotSn, loadCommands, loadRobot]);

    // Newest first, for both route and interactions - the API
    // returns them oldest-first (chronological order they actually
    // happened in), so this just reverses for display without
    // touching the underlying data or re-fetching.
    const routeNewestFirst = report ? [...report.route].reverse() : [];

    const interactionsNewestFirst = report ? [...report.interactions].reverse() : [];

    if (robotLoading) {

        return (
            <div className="page">
                <p className="empty-note"><FaSyncAlt className="spin" /> Loading robot...</p>
            </div>
        );

    }

    if (robotError || !robot) {

        return (
            <div className="page">
                <p className="empty-note">{robotError || "Robot not found."}</p>
                <Link to="/robot-fleet" className="add-btn" style={{ display: "inline-flex", marginTop: "12px" }}>
                    <FaArrowLeft /> Back to Robot Fleet
                </Link>
            </div>
        );

    }

    return (

        <div className="page robot-detail-page">

            <div className="page-header robot-detail-header">

                <div>

                    <button className="back-link" onClick={() => navigate("/robot-fleet")}>
                        <FaArrowLeft /> Back to Robot Fleet
                    </button>

                    <h2>{robot.robot_name || robot.robot_sn}</h2>

                    <p className="robot-detail-subtitle">
                        SN: {robot.robot_sn} {robot.deployment_zone ? `· ${robot.deployment_zone}` : ""}
                    </p>

                    <div className="drawer-live-status">
                        <StatusPill robot={robot} />
                        <BatteryBar pct={robot.battery_rate} />
                    </div>

                    {

                        robot.last_task_label &&

                        <p className="drawer-last-task">
                            Previous status: {robot.last_task_label}
                        </p>

                    }

                </div>

            </div>

            <div className="drawer-actions">

                {perms.add &&
                <button
                    className="action-btn action-charge"
                    onClick={handleSendToCharging}
                    disabled={sendingCharge}
                    title="Send this robot to its charging dock now"
                >
                    <FaChargingStation className={sendingCharge ? "spin" : ""} />
                    {sendingCharge ? "Sending..." : "Send to charging"}
                </button>
                }

                {perms.add &&
                <button
                    className="action-btn action-reception"
                    onClick={handleSendToReception}
                    disabled={sendingNav}
                    title={`Send this robot to ${robot.deployment_zone ? "reception" : "its configured reception point"}`}
                >
                    <FaWalking className={sendingNav ? "spin" : ""} />
                    {sendingNav ? "Sending..." : "Send to reception"}
                </button>
                }

            </div>

            {commandNote && (

                <p className={`command-note ${commandNote.ok ? "ok" : "err"}`}>
                    {commandNote.ok ? <FaCheckCircle /> : <FaBan />} {commandNote.text}
                </p>

            )}

            <div className="drawer-date-picker">

                <label htmlFor="report-date">Report date</label>

                <input
                    id="report-date"
                    type="date"
                    value={reportDate}
                    max={getTodayDateString()}
                    onChange={(e) => setReportDate(e.target.value)}
                />

            </div>

            {loading && (
                <p className="empty-note"><FaSyncAlt className="spin" /> Loading report...</p>
            )}

            {!loading && error && (
                <p className="empty-note">{error}</p>
            )}

            {!loading && !error && report && (

                <div className="robot-detail-grid">

                    <div className="drawer-stat-grid robot-detail-stat-grid">

                        <div className="drawer-stat">
                            <div className="value">
                                {report.battery_start ?? "-"}% → {report.battery_end ?? "-"}%
                            </div>
                            <div className="label">Battery start → end</div>
                        </div>

                        <div className="drawer-stat">
                            <div className="value">{formatDuration(report.time_idle_seconds)}</div>
                            <div className="label">Total idle time</div>
                        </div>

                        <div className="drawer-stat">
                            <div className="value">{report.interaction_count}</div>
                            <div className="label">Passenger interactions</div>
                        </div>

                        <div className="drawer-stat">
                            <div className="value">{report.battery_min ?? "-"}%</div>
                            <div className="label">Lowest battery</div>
                        </div>

                        <div className="drawer-stat">
                            <div className="value">{formatDuration(report.time_charging_seconds)}</div>
                            <div className="label">Time charging</div>
                        </div>

                        <div className="drawer-stat">
                            <div className="value">{report.emergency_count}</div>
                            <div className="label">Emergency events</div>
                        </div>

                    </div>

                    <div className="drawer-section">

                        <h4><FaMapMarkerAlt /> Route (named waypoints visited) - most recent first</h4>

                        {routeNewestFirst.length === 0 && (
                            <p className="empty-note">No location data recorded for this day.</p>
                        )}

                        <div className="route-list route-list-full">

                            {routeNewestFirst.map((stop, idx) => (

                                <div className="route-item" key={idx}>
                                    <span className="pos">{stop.pos_name}</span>
                                    <span className="time">
                                        {formatTime(stop.arrived_at)} – {stop.left_at ? formatTime(stop.left_at) : "now"}
                                        {" "}({formatDuration(stop.duration_seconds)})
                                    </span>
                                </div>

                            ))}

                        </div>

                    </div>

                    <div className="drawer-section">

                        <h4>Idle periods</h4>

                        {report.idle_periods.length === 0 && (
                            <p className="empty-note">No idle periods recorded for this day.</p>
                        )}

                        <div className="idle-list">

                            {[...report.idle_periods].reverse().map((period, idx) => (

                                <div className="idle-item" key={idx}>
                                    <span>{formatTime(period.started_at)} – {period.ended_at ? formatTime(period.ended_at) : "ongoing"}</span>
                                    <span className="time">{formatDuration(period.duration_seconds)}</span>
                                </div>

                            ))}

                        </div>

                    </div>

                    <div className="drawer-section">

                        <div className="robot-detail-section-header">

                            <h4>Passenger interaction log - most recent first</h4>

                            <button
                                className="download-btn"
                                onClick={() => downloadInteractionsCsv(robot, reportDate, interactionsNewestFirst)}
                                disabled={interactionsNewestFirst.length === 0}
                                title="Download this day's interaction log as CSV"
                            >
                                <FaDownload /> Download CSV
                            </button>

                        </div>

                        {interactionsNewestFirst.length === 0 && (
                            <p className="empty-note">No passenger interactions logged for this day.</p>
                        )}

                        <div className="interaction-list interaction-list-full">

                            {interactionsNewestFirst.map((item) => (

                                <div
                                    className={`interaction-item ${item.was_emergency ? "emergency" : ""}`}
                                    key={item.id}
                                >
                                    <div className="q">Q: {item.passenger_question}</div>
                                    <div className="a">A: {item.robot_answer || "-"}</div>
                                    <div className="meta">
                                        <span>{formatTime(item.created_at)}</span>
                                        <span>{item.was_emergency ? `⚠ ${item.emergency_type}` : (item.navigate_to || "")}</span>
                                    </div>
                                </div>

                            ))}

                        </div>

                    </div>

                </div>

            )}

            <div className="drawer-section">

                <h4>Recent commands</h4>

                {commands.length === 0 && (
                    <p className="empty-note">No commands sent to this robot yet.</p>
                )}

                <div className="command-list">

                    {commands.map((cmd) => (

                        <div className={`command-item ${cmd.status}`} key={cmd.id}>

                            <span className="command-type">
                                {cmd.command_type === "go_charging" ? <FaChargingStation /> : <FaWalking />}
                                {" "}
                                {cmd.command_type === "go_charging"
                                    ? "Sent to charging"
                                    : `Sent to ${cmd.target_pos_name}`}
                            </span>

                            <span className={`command-tag ${cmd.triggered_by}`}>
                                {cmd.triggered_by === "auto" ? "auto (low battery)" : `manual · ${cmd.requested_by || "admin"}`}
                            </span>

                            <span className="command-time">{formatDateTime(cmd.created_at)}</span>

                            <span className={`command-status ${cmd.status}`}>
                                {cmd.status === "sent" ? <FaCheckCircle /> : <FaBan />} {cmd.status}
                            </span>

                            <OutcomeBadge outcome={cmd.outcome} />

                            {(cmd.orion_msg_id || cmd.orion_req_id) && (

                                <span className="command-orion-ids" title="Give these to OrionStar support if this command needs escalating">
                                    {cmd.orion_msg_id && `msg_id: ${cmd.orion_msg_id}`}
                                    {cmd.orion_msg_id && cmd.orion_req_id && "  ·  "}
                                    {cmd.orion_req_id && `req_id: ${cmd.orion_req_id}`}
                                </span>

                            )}

                        </div>

                    ))}

                </div>

            </div>

        </div>

    );

}

export default RobotDetail;
